import { isNullOrUndefined } from './HelperUtil';

/**
 * Gets the periods (months) between 2 dates.
 * @param {DateTime} date1 - The first date to compare.
 * @param {DateTime} date2 - The second date to compare.
 * @returns {number} The number of months between 2 dates
 */
export const GetPeriodsBetween = (date1, date2) => {
    return Math.Abs(((date1.getYear() - date2.getYear()) * 12) + date1.getMonth() - date2.getMonth()) + 1;
}

/**
 * Takes the task and updates its parent task package. Parent task package will then 
 * update its own parent until we reach the root function (null case)
 * @param {Task} task - The newly returned task from the server side. 
 */
export const updateParentTaskPackage = (task) => {
    let parentTaskPackage = null
    if (task.parent !== "") {
        parentTaskPackage = task.getParentTaskPackage()
    }
    if (!isNullOrUndefined(parentTaskPackage)) {
        parentTaskPackage.plannedCost = getUpdatedCost(parentTaskPackage)
        parentTaskPackage.plannedStartDate = getUpdatedStartDate(parentTaskPackage)
        parentTaskPackage.plannedEndDate = getUpdatedEndDate(parentTaskPackage)

        parentTaskPackage.updateTaskPackage();
    }
}

/**
 * Returns the planend cost of parentTaskPackage by iterating over its children tasks and
 * children task packages and summing their planned costs together.
 * @param {TaskPackage} parentTaskPackage - The parent task package whose cost we want to update.
 * @returns {string} Sum of children task's and children task package's planned costs.
 */
export const getUpdatedCost = (parentTaskPackage) => {
    let newTotalCost = 0
    let mappedTaskPackageCosts = parentTaskPackage.taskPackages.map(tp => tp.plannedCost === undefined ? 0 : parseInt(tp.plannedCost))
    newTotalCost += mappedTaskPackageCosts.reduce((acc, cum) => acc + cum, 0);
    let mappedTaskCosts = parentTaskPackage.tasks.map(t => t.plannedCost === undefined ? 0 : parseInt(t.plannedCost))
    newTotalCost += mappedTaskCosts.reduce((acc, cum) => acc + cum, 0);
    return newTotalCost.toString();
}

/**
 * Returns the plannedStartdate of parentTaskPackage by iterating over its children tasks and
 * children task packages and finding the minimum start date.
 * @param {TaskPackage} parentTaskPackage - The parent task package whose start date we want to find
 * @returns {string} Minimum start date of child tasks and child task packages.
 */
export const getUpdatedStartDate = (parentTaskPackage) => {
    let startDate = null
    startDate = getMinDate(startDate, parentTaskPackage.taskPackages)
    startDate = getMinDate(startDate, parentTaskPackage.tasks)
    return startDate.toISOString()
}

/**
 * Returns the minimum start date from taskPackages by mapping them to their plannedStartDates and comparing the resulting 
 * min date against the current estimated minimum start date.
 * @param {[TaskPackage]} startDate - The current estimated minimum start date
 * @param {[Task]|[TaskPackage]} taskPackages - The task or task package that we want to find the minimum date from
 * @returns {DateTime} Minimum start date of child tasks and child task packages.
 */
export const getMinDate = (startDate, taskPackages) => {
    let mappedDatesMin = taskPackages.map(tp => isNullOrUndefined(tp.plannedStartDate) ? null : new Date(tp.plannedStartDate)).filter(date => date !== null);
    let minDate = mappedDatesMin.length > 0 ? [new Date(Math.min.apply(null, mappedDatesMin))] : []
    if (startDate !== null) {
        minDate = minDate.concat([startDate])
    }
    return minDate.length > 0 ? new Date(Math.min.apply(null, minDate)) : null
}

/**
 * Returns the plannedEndDate of parentTaskPackage by iterating over its children tasks and
 * children task packages and finding the maximum end date.
 * @param {TaskPackage} parentTaskPackage - The parent task package whose end date we want to find
 * @returns {string} Maximum end date of child tasks and child task packages.
 */
export const getUpdatedEndDate = (parentTaskPackage) => {
    let endDate = null
    endDate = getMaxDate(endDate, parentTaskPackage.taskPackages)
    endDate = getMaxDate(endDate, parentTaskPackage.tasks)
    return endDate.toISOString()
}

/**
 * Returns the maximum end date from taskPackages by mapping them to their planneEndDates and comparing the resulting 
 * max date against the current estimated maximum end date.
 * @param {[TaskPackage]} startDate - The current estimated maximum end date
 * @param {[Task]|[TaskPackage]} taskPackages - The task or task package that we want to find the maximum date from
 * @returns {DateTime} Maximum end date of child tasks and child task packages.
 */
export const getMaxDate = (endDate, taskPackages) => {
    let mappedDatesMax = taskPackages.map(tp => isNullOrUndefined(tp.plannedEndDate) ? null : new Date(tp.plannedEndDate)).filter(date => date !== null);
    let maxDate = mappedDatesMax.length > 0 ? [new Date(Math.max.apply(null, mappedDatesMax))] : []
    if (endDate !== null) {
        maxDate = maxDate.concat([endDate])
    }
    return maxDate.length > 0 ? new Date(Math.max.apply(null, maxDate)) : null
}

/**
 * Returns the plannedStartPeriod of parentTaskPackage by iterating over its children tasks and
 * children task packages and finding the minimum start period.
 * @param {TaskPackage} parentTaskPackage - The parent task package whose start period we want to find
 * @returns {string} Earliest start period of child tasks and child task packages.
 */
export const getUpdatedStartPeriod = (parentTaskPackage) => {
    let startPeriod = isNullOrUndefined(parentTaskPackage.plannedStartPeriod) ? 0 : parseInt(parentTaskPackage.plannedStartPeriod)
    startPeriod = Math.min(startPeriod, Math.min(...parentTaskPackage.taskPackages.map(tp => tp.plannedStartPeriod)))
    startPeriod = Math.min(startPeriod, Math.min(...parentTaskPackage.tasks.map(t => t.plannedStartPeriod)))
    return startPeriod
}

/**
 * Returns the plannedEndPeriod of parentTaskPackage by iterating over its children tasks and
 * children task packages and finding the maximum end period.
 * @param {TaskPackage} parentTaskPackage - The parent task package whose end period we want to find
 * @returns {string} Latest end period of child tasks and child task packages.
 */
export const getUpdatedEndPeriod = (parentTaskPackage) => {
    let endPeriod = isNullOrUndefined(parentTaskPackage.plannedEndPeriod) ? 0 : parseInt(parentTaskPackage.plannedEndPeriod)
    endPeriod = Math.max(endPeriod, Math.max(...parentTaskPackage.taskPackages.map(tp => tp.plannedEndPeriod)))
    endPeriod = Math.max(endPeriod, Math.max(...parentTaskPackage.tasks.map(t => t.plannedEndPeriod)))
    return endPeriod
}
